import React, { useCallback, useEffect } from "react";
import "./App.less";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import { ConfigProvider, Layout, Spin } from "antd";
import faIR from "antd/es/locale/fa_IR";
import { isUndefined } from "util";
import { Header, Footer } from "./components";
import { useQuery } from "./hooks";
import { useAppDispatch, useAppSelector } from "../application/store";
import Cart from "./pages/Cart";
import Category from "./pages/Category";
import ChangePassword from "./pages/ChangePassword";
import Home from "./pages/Home";
import LoginAuth from "./pages/LoginAuth";
import LoginRegister from "./pages/LoginRegister";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Product from "./pages/Product";
import Profile from "./pages/Profile";
import RegisterForm from "./pages/RegisterForm";
import Terms from "./pages/Terms";
import VerifyCode from "./pages/VerifyCode";
import { checkAuthentication } from "../application/auth-slice";
import { isMobile } from "react-device-detect";
import AboutUs from "./pages/AboutUs";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import VerifyPayment from "./pages/VerifyPayment";
import SelectAddress from "./pages/SelectAddress";
import Shipping from "./pages/Shipping";
import useWindowDimensions from "./hooks/useWindowDimensions";

const LoginRegisterRoute = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  if (isAuthenticated !== "authenticated") return <LoginRegister />;
  return <Redirect to="/" />;
};

const LoginAuthRoute = () => {
  const { isRegistered } = useAppSelector((state) => state.loginRegister);
  const query = useQuery();
  if (isUndefined(isRegistered) === false) return <LoginAuth />;
  return <Redirect to={`/login-register?back=${query.get("back")}`} />;
};

const RegisterFormRoute = () => {
  const { isRegistered } = useAppSelector((state) => state.loginRegister);
  const query = useQuery();
  if (isUndefined(isRegistered) === false) return <RegisterForm />;
  return <Redirect to={`/login-register?back=${query.get("back")}`} />;
};

const VerifyCodeRoute = () => {
  const { isRegistered } = useAppSelector((state) => state.loginRegister);
  const query = useQuery();
  if (isUndefined(isRegistered) === false) return <VerifyCode />;
  return <Redirect to={`/login-register?back=${query.get("back")}`} />;
};

const ProfileRoute = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  if (isAuthenticated === "authenticated") return <Profile />;
  return <Redirect to="/login-register?back=/profile" />;
};

const SelectAddressRoute = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { items } = useAppSelector((state) => state.cart);
  if (isAuthenticated === "authenticated") {
    if (items.length > 0) return <SelectAddress />;
    return <Redirect to="/cart" />;
  }
  return <Redirect to="/login-register?back=/select-address" />;
};

const ShippingRoute = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const query = useQuery();
  const address = query.get("address");
  if (isAuthenticated === "authenticated") {
    if (address !== null) return <Shipping />;
    return <Redirect to="/select-address" />;
  }
  return <Redirect to="/login-register?back=/cart" />;
};

export default function App() {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { height } = useWindowDimensions();

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    stableDispatch(checkAuthentication());
  }, [stableDispatch]);

  return (
    <ConfigProvider locale={faIR} direction="rtl" componentSize="large">
      <BrowserRouter>
        <Switch>
          <Route path="/verify-payment" component={VerifyPayment} exact />
          <Route path="/login-register" component={LoginRegisterRoute} exact />
          <Route
            path="/login-auth/:phone([0-9]{11})"
            component={LoginAuthRoute}
            exact
          />
          <Route
            path="/register-form/:phone([0-9]{11})"
            component={RegisterFormRoute}
            exact
          />
          <Route
            path="/verify-code/:type(login|register|forgot-password)/:phone([0-9]{11})"
            component={VerifyCodeRoute}
            exact
          />
          <Route
            path="/change-password/:phone([0-9]{11})/:token"
            component={ChangePassword}
            exact
          />
          <Layout>
            <Header />
            <Layout.Content
              className={isMobile ? "content-mobile" : "content-web"}
              style={{ minHeight: height - 260 }}
            >
              {isAuthenticated === "initial" ? (
                <Spin />
              ) : (
                <Switch>
                  <Route path="/" component={Home} exact />
                  <Route path="/cart" component={Cart} exact />
                  <Route
                    path="/profile/:activeTab(|account|addresses|orders)"
                    exact
                    render={({ history, location, match }) => {
                      if (match.params.activeTab === "orders") {
                        const query = new URLSearchParams(location.search);
                        const activeTab = query.get("activeTab");
                        if (
                          activeTab !== null &&
                          activeTab !== "pending" &&
                          activeTab !== "paid" &&
                          activeTab !== "posted" &&
                          activeTab !== "delivered"
                        ) {
                          return <NotFound />;
                        }
                      }
                      return <ProfileRoute />;
                    }}
                  />
                  <Route path="/profile" component={ProfileRoute} exact />
                  <Route
                    path="/category/:id/:title?"
                    component={Category}
                    exact
                  />
                  <Route
                    path="/product/:id/:title?"
                    component={Product}
                    exact
                  />
                  <Route path="/terms" component={Terms} exact />
                  <Route
                    path="/privacy-policy"
                    component={PrivacyPolicy}
                    exact
                  />
                  <Route path="/about" component={AboutUs} exact />
                  <Route path="/contact-us" component={Contact} exact />
                  <Route path="/faq" component={FAQ} exact />
                  <Route
                    path="/select-address"
                    component={SelectAddressRoute}
                    exact
                  />
                  <Route path="/shipping" component={ShippingRoute} exact />
                  <Route component={NotFound} />
                </Switch>
              )}
            </Layout.Content>
            <Footer />
          </Layout>
        </Switch>
      </BrowserRouter>
    </ConfigProvider>
  );
}
